import React, { useState } from 'react';
import { TrendingUp } from 'lucide-react';

export default function TrafficTimelineChart({ timeline }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!timeline || timeline.length === 0) {
    return (
      <div className="glass-card" style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
        No concurrency data available for this time window.
      </div>
    );
  }

  // Chart dimensions
  const svgWidth = 800;
  const svgHeight = 240;
  const padding = { top: 20, right: 30, bottom: 40, left: 45 };
  const graphWidth = svgWidth - padding.left - padding.right;
  const graphHeight = svgHeight - padding.top - padding.bottom;

  // Max value calculation
  const maxVal = Math.max(...timeline.map(d => Math.max(d.peakSessions || 0, d.activeSessions || 0, 10)), 20);

  // Generate SVG coordinate points
  const points = timeline.map((d, index) => {
    const x = padding.left + (index / (timeline.length - 1)) * graphWidth;
    const yActive = padding.top + graphHeight - ((d.activeSessions || 0) / maxVal) * graphHeight;
    const yPeak = padding.top + graphHeight - ((d.peakSessions || 0) / maxVal) * graphHeight;
    return { ...d, x, yActive, yPeak };
  });

  // Construct Area & Line SVG paths
  const lineActivePath = points.reduce((acc, p, i) => `${acc} ${i === 0 ? 'M' : 'L'} ${p.x} ${p.yActive}`, '');
  const linePeakPath = points.reduce((acc, p, i) => `${acc} ${i === 0 ? 'M' : 'L'} ${p.x} ${p.yPeak}`, '');
  const areaActivePath = `${lineActivePath} L ${points[points.length - 1].x} ${padding.top + graphHeight} L ${points[0].x} ${padding.top + graphHeight} Z`;

  const hoveredPoint = hoveredIndex !== null ? points[hoveredIndex] : null;

  return (
    <div className="glass-card" style={{ padding: '24px', background: '#FFFFFF' }}>
      
      {/* Chart Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <TrendingUp size={18} color="#F84122" />
            <h2 style={{ fontSize: '16px', fontWeight: '800', color: '#0F172A', margin: 0 }}>Traffic & Concurrency Timeline</h2>
          </div>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px', margin: 0 }}>
            Active sessions & peak capacity across time buckets (24H Trend)
          </p>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', height: '4px', background: '#F84122', borderRadius: '2px' }} />
            <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Active Sessions</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '12px', height: '2px', background: '#0F172A', borderStyle: 'dashed' }} />
            <span style={{ color: 'var(--text-secondary)', fontWeight: '600' }}>Peak Capacity</span>
          </div>
        </div>
      </div>

      {/* SVG Responsive Container */}
      <div style={{ position: 'relative', width: '100%', overflow: 'hidden' }}>
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          <defs>
            <linearGradient id="orangeGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F84122" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#F84122" stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => {
            const y = padding.top + graphHeight * (1 - ratio);
            const val = Math.round(maxVal * ratio);
            return (
              <g key={i}>
                <line
                  x1={padding.left}
                  y1={y}
                  x2={svgWidth - padding.right}
                  y2={y}
                  stroke="#E2E8F0"
                  strokeDasharray="4 4"
                />
                <text
                  x={padding.left - 8}
                  y={y + 4}
                  fill="#94A3B8"
                  fontSize="11"
                  textAnchor="end"
                  fontFamily="var(--font-mono)"
                >
                  {val}
                </text>
              </g>
            );
          })}

          {/* Area fill */}
          <path d={areaActivePath} fill="url(#orangeGradient)" />

          {/* Peak Concurrency Dashed Line */}
          <path
            d={linePeakPath}
            fill="none"
            stroke="#0F172A"
            strokeWidth="2"
            strokeDasharray="4 4"
            opacity="0.8"
          />

          {/* Active Sessions Solid Line */}
          <path
            d={lineActivePath}
            fill="none"
            stroke="#F84122"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Interactive hover points and vertical tracking bar */}
          {points.map((p, i) => (
            <g key={i} onMouseEnter={() => setHoveredIndex(i)} style={{ cursor: 'pointer' }}>
              <rect
                x={p.x - graphWidth / points.length / 2}
                y={padding.top}
                width={graphWidth / points.length}
                height={graphHeight}
                fill="transparent"
              />

              {hoveredIndex === i && (
                <>
                  <line
                    x1={p.x}
                    y1={padding.top}
                    x2={p.x}
                    y2={padding.top + graphHeight}
                    stroke="#F84122"
                    strokeWidth="1.5"
                    strokeDasharray="2 2"
                  />
                  <circle cx={p.x} cy={p.yActive} r="6" fill="#F84122" stroke="#FFFFFF" strokeWidth="2" />
                  <circle cx={p.x} cy={p.yPeak} r="5" fill="#0F172A" stroke="#FFFFFF" strokeWidth="2" />
                </>
              )}
            </g>
          ))}

          {/* X Axis Time Labels */}
          {points.filter((_, i) => i % 4 === 0 || i === points.length - 1).map((p, i) => {
            const timeLabel = new Date(p.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            return (
              <text
                key={i}
                x={p.x}
                y={svgHeight - 12}
                fill="#64748B"
                fontSize="11"
                textAnchor="middle"
                fontFamily="var(--font-mono)"
              >
                {timeLabel}
              </text>
            );
          })}
        </svg>

        {/* Hover Tooltip Popup */}
        {hoveredPoint && (
          <div style={{
            position: 'absolute',
            left: `${(hoveredPoint.x / svgWidth) * 100}%`,
            top: '20px',
            transform: 'translateX(-50%)',
            background: '#FFFFFF',
            border: '1px solid #CBD5E1',
            borderRadius: '8px',
            padding: '10px 14px',
            boxShadow: '0 10px 25px -3px rgba(15, 23, 42, 0.12)',
            pointerEvents: 'none',
            zIndex: 10,
            fontSize: '12px',
            minWidth: '160px'
          }}>
            <div style={{ fontWeight: '700', color: '#0F172A', marginBottom: '6px', borderBottom: '1px solid #E2E8F0', paddingBottom: '4px' }}>
              {new Date(hoveredPoint.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#F84122', fontWeight: '700', marginBottom: '3px' }}>
              <span>Active Sessions:</span>
              <span>{hoveredPoint.activeSessions}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: '#0F172A', fontWeight: '600' }}>
              <span>Peak Window:</span>
              <span>{hoveredPoint.peakSessions}</span>
            </div>
            {hoveredPoint.completedInWindow !== undefined && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#059669', marginTop: '4px', fontSize: '11px' }}>
                <span>Completed:</span>
                <span>{hoveredPoint.completedInWindow}</span>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
